import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { isEqual } from '@sama/micro-dash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import {
  X,
  Y,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { getPointString } from '../abstract-shape-helpers';
import { AbstractShapeComponent } from '../abstract-shape.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- svg needs attribute selectors
  selector: '[h3Arrow]',
  templateUrl: './arrow.component.html',
  styleUrls: [
    './arrow.component.scss',
    '../line-shape-styles.scss',
    '../../shape-parts/outline-host.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArrowComponent extends AbstractShapeComponent implements OnInit {
  arrowheadPointString$!: Observable<string>;
  arrowheadTransform$!: Observable<string>;

  override ngOnInit() {
    super.ngOnInit();
    this.arrowheadPointString$ = combineLatest([
      this.lastTwoPoints$(),
      this.scale$,
    ]).pipe(
      map(([[lastPoint, _secondLastPoint], scale]) =>
        getPointString(getArrowheadCoordinates(lastPoint, scale)),
      ),
      distinctUntilChanged(),
    );

    this.arrowheadTransform$ = this.lastTwoPoints$().pipe(
      map((points) => getArrowheadTransformString(points)),
    );
  }

  private lastTwoPoints$() {
    return this.points$.pipe(
      filter((points) => {
        const [p2, p1] = points.slice(-2);
        return points.length >= 2 && !isEqual(p1, p2);
      }),
      map((points) => points.slice(-2).reverse()),
    );
  }
}

function getArrowheadTransformString([lastPoint, secondLastPoint]: Point2D[]) {
  // angle is relative to the vertical axis
  const angle =
    (Math.atan2(
      secondLastPoint[Y] - lastPoint[Y],
      secondLastPoint[X] - lastPoint[X],
    ) *
      180) /
      Math.PI +
    90;
  // rotate by that much around the last point
  return `rotate(${angle} ${lastPoint[X]} ${lastPoint[Y]})`;
}

function getArrowheadCoordinates(top: Point2D, scale: number): Point2D[] {
  const sideLength = Math.max((1 / scale) * 13, 2);
  const height = (sideLength / 2) * Math.sqrt(3);
  const rightCorner = [top[X] + sideLength / 2, top[Y] - height].map(
    Math.round,
  ) as Point2D;
  const leftCorner = [top[X] - sideLength / 2, top[Y] - height].map(
    Math.round,
  ) as Point2D;

  return [leftCorner, top, rightCorner];
}
